import React, { useState, useEffect } from 'react';
import { User, UserPlus, Save, Mail, CreditCard, Bell, Wallet, AlertTriangle, Network, FileText } from 'lucide-react';
import { useUser, useAuthStore } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { AgreementCard } from '../components/physicians/AgreementCard';
import { toast } from '../lib/toast';
import { useTenantBranding } from '../hooks/useTenantBranding-runtime';
import { PaymentMethodsManager } from '../components/payments/PaymentMethodsManager';
import { PrescriberNetworkSettings } from '../components/physicians/PrescriberNetworkSettings';
import StateLicensesSection from '../components/physicians/StateLicensesSection';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  phone: string;
  clinicName?: string;
  npiNumber?: string;
  companyName?: string;
  address?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  clinicAddress?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
}

// interface PasswordFormData {
//   currentPassword: string;
//   newPassword: string;
//   confirmPassword: string;
// }

interface InviteFormData {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  phone: string;
}

interface PaymentFormData {
  paymentInstructions: string;
}

/**
 * Seed the profile form from the authenticated user.
 *
 * Shared by the initial `useState` and the rehydrate effect below so the two
 * can't drift apart — the bug this addresses was caused by only one of those
 * existing.
 */
const buildProfileData = (user: ReturnType<typeof useUser>): ProfileFormData => ({
  firstName: user?.profile?.firstName || '',
  lastName: user?.profile?.lastName || '',
  phone: user?.profile?.phone || '',
  clinicName: user?.profile?.clinicName || '',
  npiNumber: user?.physicianData?.npiNumber || '',
  companyName: user?.distributorData?.companyName || user?.supervisorData?.companyName || '',
  address: {
    street: user?.distributorData?.address?.street || user?.supervisorData?.address?.street || '',
    street2: user?.distributorData?.address?.street2 || user?.supervisorData?.address?.street2 || '',
    city: user?.distributorData?.address?.city || user?.supervisorData?.address?.city || '',
    state: user?.distributorData?.address?.state || user?.supervisorData?.address?.state || '',
    zipCode: user?.distributorData?.address?.zipCode || user?.supervisorData?.address?.zipCode || ''
  },
  clinicAddress: {
    street: user?.physicianData?.clinicAddress?.street || '',
    street2: user?.physicianData?.clinicAddress?.street2 || '',
    city: user?.physicianData?.clinicAddress?.city || '',
    state: user?.physicianData?.clinicAddress?.state || '',
    zipCode: user?.physicianData?.clinicAddress?.zipCode || ''
  }
});

const Settings: React.FC = () => {
  const user = useUser();
  const { branding } = useTenantBranding();
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'admin' | 'payment' | 'payment-methods' | 'network' | 'contracts'>('profile');
  
  // Profile form state
  const [profileData, setProfileData] = useState<ProfileFormData>(() => buildProfileData(user));
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState({
    receiveEmails: true,
    receiveTexts: false
  });
  const [_notificationLoading, setNotificationLoading] = useState(false);

  // Password form state (commented out - functionality disabled)
  // const [passwordData, setPasswordData] = useState<PasswordFormData>({
  //   currentPassword: '',
  //   newPassword: '',
  //   confirmPassword: ''
  // });
  // const [passwordLoading, setPasswordLoading] = useState(false);
  // const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // const [showPasswords, setShowPasswords] = useState({
  //   current: false,
  //   new: false,
  //   confirm: false
  // });

  // Admin invite form state
  const [inviteData, setInviteData] = useState<InviteFormData>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'admin',
    phone: ''
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Payment instructions form state — every payout-receiving role stores them
  // on their own role-specific sub-document.
  const currentPaymentInstructions =
    user?.supervisorData?.paymentInstructions ||
    user?.distributorData?.paymentInstructions ||
    user?.physicianData?.paymentInstructions ||
    '';
  const [paymentData, setPaymentData] = useState<PaymentFormData>({
    paymentInstructions: currentPaymentInstructions
  });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Rehydrate the profile form once the authenticated user actually arrives.
  //
  // profileData is seeded in a useState initializer, which runs only on the
  // first render. When Settings mounts before the auth store has resolved
  // (fresh load, slow network), every field seeded to '' and previously stayed
  // that way for the life of the page. Because handleProfileUpdate submits the
  // WHOLE object regardless of which field was edited, that stale blank
  // npiNumber then read as an NPI *change* server-side and the entire save was
  // rejected with "NPI is required" — so editing a clinic name or address
  // silently failed with an unrelated NPI error (MD-2776, MD-2811, MD-2826).
  // The blank State input separately rendered its "FL" placeholder, which read
  // as the address having reverted to Florida.
  //
  // Keyed on user._id rather than the individual fields: this fires when the
  // user resolves or switches (impersonation) but not on unrelated store
  // updates, so it can't stomp on edits being typed.
  useEffect(() => {
    if (!user?._id) return;
    setProfileData(buildProfileData(user));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  // Update payment data when user data changes
  useEffect(() => {
    const instructions =
      user?.supervisorData?.paymentInstructions ||
      user?.distributorData?.paymentInstructions ||
      user?.physicianData?.paymentInstructions;
    if (instructions) {
      setPaymentData({ paymentInstructions: instructions });
    }
  }, [
    user?.supervisorData?.paymentInstructions,
    user?.distributorData?.paymentInstructions,
    user?.physicianData?.paymentInstructions
  ]);

  // Load notification preferences on mount
  useEffect(() => {
    loadNotificationPreferences();
  }, []);

  const loadNotificationPreferences = async () => {
    try {
      const response = await apiClient.getNotificationPreferences();
      if (response.success) {
        setNotificationPreferences(response.data);
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    }
  };

  const handleNotificationToggle = async (field: 'receiveEmails' | 'receiveTexts', value: boolean) => {
    const updatedPreferences = {
      ...notificationPreferences,
      [field]: value
    };
    
    setNotificationPreferences(updatedPreferences);
    
    try {
      setNotificationLoading(true);
      const response = await apiClient.updateNotificationPreferences(updatedPreferences);
      
      if (response.success) {
        toast.success('Notification preferences updated');
      } else {
        // Revert on failure
        setNotificationPreferences(notificationPreferences);
        toast.error(response.error || 'Failed to update preferences');
      }
    } catch (error) {
      // Revert on failure
      setNotificationPreferences(notificationPreferences);
      toast.error('Failed to update notification preferences');
    } finally {
      setNotificationLoading(false);
    }
  };

  // Deep link from the dashboard banner: /settings?tab=contracts. Above the
  // early return so the hook count doesn't change when the user arrives late.
  useEffect(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    if (tab === 'contracts') setActiveTab('contracts');
  }, []);

  if (!user) {
    return null;
  }

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileMessage(null);

    try {
      const result = await apiClient.updateUserProfile(user._id, profileData);
      if (result.success) {
        toast.success('Profile updated successfully!');
        setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
        // Bring the cached user up to date so the new address shows everywhere
        // without a re-login (MD-3208). Patch from the server's copy — it has
        // the normalized state code — rather than calling getCurrentUser(),
        // which would remount this page and drop the form.
        const saved = result.data;
        if (saved) {
          useAuthStore.getState().patchUser({
            ...(saved.profile && { profile: saved.profile }),
            ...(saved.physicianData && { physicianData: saved.physicianData }),
            ...(saved.distributorData && { distributorData: saved.distributorData }),
            ...(saved.supervisorData && { supervisorData: saved.supervisorData })
          });
        }
      } else {
        toast.error(result.error || 'Failed to update profile');
        setProfileMessage({ type: 'error', text: result.error || 'Failed to update profile' });
      }
    } catch (error: any) {
      toast.error('Failed to update profile');
      setProfileMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setProfileLoading(false);
    }
  };

  // const handlePasswordChange = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setPasswordLoading(true);
  //   setPasswordMessage(null);

  //   // Validation
  //   if (passwordData.newPassword !== passwordData.confirmPassword) {
  //     setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
  //     setPasswordLoading(false);
  //     return;
  //   }

  //   if (passwordData.newPassword.length < 8) {
  //     setPasswordMessage({ type: 'error', text: 'New password must be at least 8 characters long' });
  //     setPasswordLoading(false);
  //     return;
  //   }

  //   try {
  //     const result = await apiClient.changePassword(passwordData.currentPassword, passwordData.newPassword);
  //     if (result.success) {
  //       setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
  //       setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
  //     } else {
  //       setPasswordMessage({ type: 'error', text: result.error || 'Failed to change password' });
  //     }
  //   } catch (error: any) {
  //     setPasswordMessage({ type: 'error', text: 'Failed to change password' });
  //   } finally {
  //     setPasswordLoading(false);
  //   }
  // };

  const handleAdminInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteMessage(null);

    try {
      const result = await apiClient.inviteUser(inviteData);
      if (result.success) {
        toast.success('Admin invitation sent successfully!');
        setInviteMessage({ type: 'success', text: 'Admin invitation sent successfully!' });
        setInviteData({ email: '', firstName: '', lastName: '', role: 'admin', phone: '' });
      } else {
        toast.error(result.error || 'Failed to send invitation');
        setInviteMessage({ type: 'error', text: result.error || 'Failed to send invitation' });
      }
    } catch (error: any) {
      toast.error('Failed to send invitation');
      setInviteMessage({ type: 'error', text: 'Failed to send invitation' });
    } finally {
      setInviteLoading(false);
    }
  };

  const handlePaymentUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setPaymentLoading(true);
    setPaymentMessage(null);

    try {
      const result = await apiClient.updatePaymentInstructions(paymentData.paymentInstructions);
      if (result.success) {
        toast.success('Payment instructions updated successfully!');
        setPaymentMessage({ type: 'success', text: 'Payment instructions updated successfully!' });
        // Same stale-cache problem as the profile save (MD-3208). The endpoint
        // returns no user, so write the value into the sub-document it stores
        // to for this role (mirrors PUT /account-managers/payment-instructions).
        const { paymentInstructions } = paymentData;
        if (user.role === 'supervisor') {
          useAuthStore.getState().patchUser({ supervisorData: { ...user.supervisorData, paymentInstructions } as any });
        } else if (user.role === 'distributor') {
          useAuthStore.getState().patchUser({ distributorData: { ...user.distributorData, paymentInstructions } as any });
        } else {
          useAuthStore.getState().patchUser({ physicianData: { ...user.physicianData, paymentInstructions } as any });
        }
      } else {
        toast.error(result.error || 'Failed to update payment instructions');
        setPaymentMessage({ type: 'error', text: result.error || 'Failed to update payment instructions' });
      }
    } catch (error: any) {
      toast.error('Failed to update payment instructions');
      setPaymentMessage({ type: 'error', text: 'Failed to update payment instructions' });
    } finally {
      setPaymentLoading(false);
    }
  };

  // Commented out until password change functionality is enabled
  // const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
  //   setShowPasswords(prev => ({
  //     ...prev,
  //     [field]: !prev[field]
  //   }));
  // };

  const isAdmin = user.role === 'admin';
  // Inviting admins is superadmin-only (POST /users/invitations enforces it).
  const isSuperAdmin = isAdmin && user.isSuperAdmin === true;
  const isSupervisor = user.role === 'supervisor';
  const isDistributor = user.role === 'distributor';
  const isPhysician = user.role === 'physician';
  // The Contract + BAA is only executed by clinics prescribing through the
  // telemed network — telemed-only, or hybrid gap coverage.
  const usesNetwork = isPhysician && (
    (user as any).physicianData?.telemedOnly === true ||
    (user as any).physicianData?.useNetworkForUncoveredStates === true
  );

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>
          
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User className="w-4 h-4 inline mr-2" />
                Profile
              </button>
              {isPhysician && (
                <button
                  onClick={() => setActiveTab('network')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'network'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Network className="w-4 h-4 inline mr-2" />
                  Prescriber Network
                </button>
              )}
              {usesNetwork && (
                <button
                  onClick={() => setActiveTab('contracts')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'contracts'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-2" />
                  Contracts
                </button>
              )}
              {/* Temporarily disabled - fixing backend compilation issue */}
              {/* 
              <button
                onClick={() => setActiveTab('password')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'password'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Lock className="w-4 h-4 inline mr-2" />
                Password
              </button>
              */}
              {(isDistributor || isSupervisor || isPhysician) && (
                <button
                  onClick={() => setActiveTab('payment')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'payment'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <CreditCard className="w-4 h-4 inline mr-2" />
                  Payout Instructions
                </button>
              )}
              {isPhysician && (
                <button
                  onClick={() => setActiveTab('payment-methods')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'payment-methods'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Wallet className="w-4 h-4 inline mr-2" />
                  Payment Methods
                </button>
              )}
              {isSuperAdmin && (
                <button
                  onClick={() => setActiveTab('admin')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'admin'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <UserPlus className="w-4 h-4 inline mr-2" />
                  Admin Management
                </button>
              )}
            </nav>
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div>
              {/* Clinic name callout for physicians */}
              {(user?.role === 'physician' || user?.role === 'physician_readonly') && !user?.profile?.clinicName && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Clinic name missing</p>
                    <p className="mt-0.5">Please add your clinic or practice name below. It will appear on invoices and patient communications.</p>
                  </div>
                </div>
              )}
              <h2 className="text-lg font-medium text-gray-900 mb-4">Profile Information</h2>
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={profileData.firstName}
                      onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={profileData.lastName}
                      onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                {/* Clinic Name & Address - Physicians only */}
                {(user?.role === 'physician' || user?.role === 'physician_readonly') && (
                  <>
                    <div>
                      <label htmlFor="npiNumber" className="block text-sm font-medium text-gray-700">
                        NPI Number
                      </label>
                      {user?.isVerified ? (
                        <>
                          <p className="mt-1 block w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900 font-mono">
                            {profileData.npiNumber || <span className="text-gray-400">Not set</span>}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            Your NPI is locked after verification. Contact your account manager or an administrator to change it.
                          </p>
                        </>
                      ) : (
                        <>
                          <input
                            type="text"
                            id="npiNumber"
                            value={profileData.npiNumber || ''}
                            onChange={(e) => setProfileData({ ...profileData, npiNumber: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                            placeholder="10-digit NPI"
                            inputMode="numeric"
                            maxLength={10}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            You can update your NPI until your account is verified.
                          </p>
                        </>
                      )}
                    </div>

                    <div>
                      <label htmlFor="clinicName" className="block text-sm font-medium text-gray-700">
                        Clinic / Practice Name
                      </label>
                      <input
                        type="text"
                        id="clinicName"
                        value={profileData.clinicName || ''}
                        onChange={(e) => setProfileData({ ...profileData, clinicName: e.target.value })}
                        placeholder="e.g., Downtown Medical Clinic"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        This name will appear on patient invoices and communications
                      </p>
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Clinic Address</h4>
                      <div className="space-y-3">
                        <div>
                          <label htmlFor="clinicStreet" className="block text-sm font-medium text-gray-600">
                            Street Address
                          </label>
                          <input
                            type="text"
                            id="clinicStreet"
                            value={profileData.clinicAddress?.street || ''}
                            onChange={(e) => setProfileData({
                              ...profileData,
                              clinicAddress: { ...profileData.clinicAddress, street: e.target.value }
                            })}
                            placeholder="123 Medical Center Dr"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label htmlFor="clinicStreet2" className="block text-sm font-medium text-gray-600">
                            Suite / Unit / Building
                          </label>
                          <input
                            type="text"
                            id="clinicStreet2"
                            value={profileData.clinicAddress?.street2 || ''}
                            onChange={(e) => setProfileData({
                              ...profileData,
                              clinicAddress: { ...profileData.clinicAddress, street2: e.target.value }
                            })}
                            placeholder="Suite 100 (optional, max 35 chars)"
                            maxLength={35}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label htmlFor="clinicCity" className="block text-sm font-medium text-gray-600">
                              City
                            </label>
                            <input
                              type="text"
                              id="clinicCity"
                              value={profileData.clinicAddress?.city || ''}
                              onChange={(e) => setProfileData({ 
                                ...profileData, 
                                clinicAddress: { ...profileData.clinicAddress, city: e.target.value }
                              })}
                              placeholder="Miami"
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label htmlFor="clinicState" className="block text-sm font-medium text-gray-600">
                              State
                            </label>
                            <input
                              type="text"
                              id="clinicState"
                              value={profileData.clinicAddress?.state || ''}
                              onChange={(e) => setProfileData({ 
                                ...profileData, 
                                clinicAddress: { ...profileData.clinicAddress, state: e.target.value }
                              })}
                              placeholder="FL"
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                        <div className="w-1/2">
                          <label htmlFor="clinicZipCode" className="block text-sm font-medium text-gray-600">
                            ZIP Code
                          </label>
                          <input
                            type="text"
                            id="clinicZipCode"
                            value={profileData.clinicAddress?.zipCode || ''}
                            onChange={(e) => setProfileData({ 
                              ...profileData, 
                              clinicAddress: { ...profileData.clinicAddress, zipCode: e.target.value }
                            })}
                            placeholder="33130"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Company Name & Address - AMs (distributors) and supervisors */}
                {(user?.role === 'distributor' || user?.role === 'supervisor') && (
                  <>
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        value={profileData.companyName || ''}
                        onChange={(e) => setProfileData({ ...profileData, companyName: e.target.value })}
                        placeholder="e.g., ABC Medical Distribution"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Business Address</h4>
                      <div className="space-y-3">
                        <div>
                          <label htmlFor="street" className="block text-sm font-medium text-gray-600">
                            Street Address
                          </label>
                          <input
                            type="text"
                            id="street"
                            value={profileData.address?.street || ''}
                            onChange={(e) => setProfileData({
                              ...profileData,
                              address: { ...profileData.address, street: e.target.value }
                            })}
                            placeholder="123 Main Street"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label htmlFor="street2" className="block text-sm font-medium text-gray-600">
                            Suite / Unit / Building
                          </label>
                          <input
                            type="text"
                            id="street2"
                            value={profileData.address?.street2 || ''}
                            onChange={(e) => setProfileData({
                              ...profileData,
                              address: { ...profileData.address, street2: e.target.value }
                            })}
                            placeholder="Suite 100 (optional, max 35 chars)"
                            maxLength={35}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label htmlFor="city" className="block text-sm font-medium text-gray-600">
                              City
                            </label>
                            <input
                              type="text"
                              id="city"
                              value={profileData.address?.city || ''}
                              onChange={(e) => setProfileData({ 
                                ...profileData, 
                                address: { ...profileData.address, city: e.target.value }
                              })}
                              placeholder="New York"
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label htmlFor="state" className="block text-sm font-medium text-gray-600">
                              State
                            </label>
                            <input
                              type="text"
                              id="state"
                              value={profileData.address?.state || ''}
                              onChange={(e) => setProfileData({ 
                                ...profileData, 
                                address: { ...profileData.address, state: e.target.value }
                              })}
                              placeholder="NY"
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                        <div className="w-1/2">
                          <label htmlFor="zipCode" className="block text-sm font-medium text-gray-600">
                            ZIP Code
                          </label>
                          <input
                            type="text"
                            id="zipCode"
                            value={profileData.address?.zipCode || ''}
                            onChange={(e) => setProfileData({ 
                              ...profileData, 
                              address: { ...profileData.address, zipCode: e.target.value }
                            })}
                            placeholder="10001"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Notification Preferences */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <Bell className="h-5 w-5 mr-2 text-gray-400" />
                    Notification Preferences
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Choose how you'd like to receive notifications. We'll use your email ({user?.email}) and phone number above.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                        <p className="text-xs text-gray-500">Receive notifications about payouts, orders, and account updates via email</p>
                      </div>
                      <div className="ml-4">
                        <button
                          type="button"
                          disabled={true} // Disabled until AWS SES is implemented
                          onClick={() => handleNotificationToggle('receiveEmails', !notificationPreferences.receiveEmails)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                            notificationPreferences.receiveEmails ? 'bg-blue-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              notificationPreferences.receiveEmails ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                        <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">Coming Soon</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <label className="text-sm font-medium text-gray-700">SMS Notifications</label>
                        <p className="text-xs text-gray-500">Receive text message alerts for urgent notifications</p>
                      </div>
                      <div className="ml-4">
                        <button
                          type="button"
                          disabled={true} // Disabled until SMS provider is implemented
                          onClick={() => handleNotificationToggle('receiveTexts', !notificationPreferences.receiveTexts)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                            notificationPreferences.receiveTexts ? 'bg-blue-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              notificationPreferences.receiveTexts ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                        <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">Coming Soon</span>
                      </div>
                    </div>

                    {/* SMS Rate Warning */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">SMS Rate Notice</h3>
                          <div className="mt-1 text-sm text-yellow-700">
                            <p>Message and data rates may apply for SMS notifications. Standard charges from your carrier will apply. You can opt out at any time by disabling SMS notifications.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Email cannot be changed. Contact support if needed.</p>
                </div>

                {profileMessage && (
                  <div className={`p-3 rounded-md ${
                    profileMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {profileMessage.text}
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={profileLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {profileLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>

              {/* Licenses save through their own endpoint, so this sits outside
                  the profile form rather than inside it. */}
              {isPhysician && <StateLicensesSection />}
            </div>
          )}

          {/* Prescriber Network Tab (physician only) */}
          {activeTab === 'network' && isPhysician && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Prescriber Network</h2>
              <PrescriberNetworkSettings />
            </div>
          )}

          {/* Contracts Tab (network clinics) */}
          {activeTab === 'contracts' && usesNetwork && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Contracts</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Agreements your clinic has executed. You can read the full text at any time, and
                  sign it here if we don't yet hold your signature.
                </p>
              </div>
              <AgreementCard
                physicianId={user._id}
                onSigned={() => { void useAuthStore.getState().getCurrentUser(); }}
              />
            </div>
          )}

          {/* Password Tab - Temporarily disabled */}
          {/* {activeTab === 'password' && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Change Password</h2>
              <p className="text-sm text-gray-600">Password change functionality is temporarily disabled while we fix a backend issue.</p>
            </div>
          )} */}

          {/* Payment Instructions Tab */}
          {activeTab === 'payment' && (isDistributor || isSupervisor || isPhysician) && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Payout Instructions</h2>
              <p className="text-sm text-gray-600 mb-6">
                Add the payment details an administrator will use to pay you out
                (wire info, ACH details, check mailing address, etc.). Only admins can see this.
              </p>

              {paymentMessage && (
                <div className={`mb-4 p-4 rounded-md ${
                  paymentMessage.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {paymentMessage.text}
                </div>
              )}

              <form onSubmit={handlePaymentUpdate} className="space-y-4">
                <div>
                  <label htmlFor="paymentInstructions" className="block text-sm font-medium text-gray-700">
                    Payment Instructions
                  </label>
                  <textarea
                    id="paymentInstructions"
                    rows={8}
                    value={paymentData.paymentInstructions}
                    onChange={(e) => setPaymentData({ ...paymentData, paymentInstructions: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your payment instructions including wire transfer details, bank information, preferred payment methods, etc."
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Include any relevant payment details such as wire transfer information, preferred payment methods, billing addresses, etc.
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={paymentLoading}
                    className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                      paymentLoading 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    }`}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {paymentLoading ? 'Saving...' : 'Save Payment Instructions'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Admin Management Tab */}
          {activeTab === 'admin' && isSuperAdmin && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Admin Management</h2>
              <p className="text-sm text-gray-600 mb-6">
                Invite other administrators to manage {branding.companyName || 'the system'}. 
                They will receive an email invitation to set up their account.
              </p>
              
              <form onSubmit={handleAdminInvite} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="inviteFirstName" className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="inviteFirstName"
                      value={inviteData.firstName}
                      onChange={(e) => setInviteData({ ...inviteData, firstName: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="inviteLastName" className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="inviteLastName"
                      value={inviteData.lastName}
                      onChange={(e) => setInviteData({ ...inviteData, lastName: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="inviteEmail" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="inviteEmail"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="invitePhone" className="block text-sm font-medium text-gray-700">
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    id="invitePhone"
                    value={inviteData.phone}
                    onChange={(e) => setInviteData({ ...inviteData, phone: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="inviteRole" className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <select
                    id="inviteRole"
                    value={inviteData.role}
                    onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="admin">Administrator</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Administrators have full access to manage users, products, and system settings.
                  </p>
                </div>

                {inviteMessage && (
                  <div className={`p-3 rounded-md ${
                    inviteMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {inviteMessage.text}
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={inviteLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {inviteLoading ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Payment Methods Tab (Physicians) */}
          {activeTab === 'payment-methods' && isPhysician && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Payment Methods</h2>
              <p className="text-sm text-gray-600 mb-6">
                Manage your saved payment methods. Your default card will be used for automatic invoice payments.
              </p>

              <PaymentMethodsManager />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Settings;
